import { h, Component, Host, State, Element, Prop, Watch, Listen, Method } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../common/colorable-component";
import { BottomDrawerSchemeDefinition, BottomDrawerSchemeName } from "./constants/bottom-drawer-types";
import { BOTTOM_DRAWER_SCHEME_CONFIG, BOTTOM_DRAWER_SCHEME_MAP } from "./constants/bottom-drawer-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { KeyIds } from "../../constants/keys";
import { BottomDrawerSchemeConfiguration } from "../../constants/colors";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 */
@Component({
  tag: "eon-ui-bottom-drawer",
  styleUrl: "bottom-drawer.scss",
  shadow: true
})
export class BottomDrawer implements ColorableComponent<BottomDrawerSchemeConfiguration<BottomDrawerSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: BottomDrawerSchemeConfiguration<BottomDrawerSchemeDefinition> = BOTTOM_DRAWER_SCHEME_CONFIG;
  schemeMap = BOTTOM_DRAWER_SCHEME_MAP;
  header: HTMLElement;

  /**
   * Text in header area
   */
  @Prop() headerText: string;

  /**
   * Text in close area
   */
  @Prop() closeText: string;

  /**
   * Centered header text
   */
  @Prop() headlineCentered: boolean;

  /**
   * Set the maximum Content width of your Website including margin left and right
   */
  @Prop() maximumContentWidth: number;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: BottomDrawerSchemeDefinition;

  /**
   * Is bottom drawer opened or closed
   */
  @Prop({ reflect: true }) isOpened: boolean;

  /**
   * Defines if text should be repositioned on large screens
   */
  @State() isLargeScreen: boolean;

  @Element() hostElement: HTMLEonUiBottomDrawerElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: BottomDrawerSchemeName;

  /**
   * Method to evaluate the component height
   */
  @Method()
  async getHeight() {
    return await this.header.getBoundingClientRect().height;
  }

  componentWillLoad() {
    this.applyScheme();
    this.checkScreenSize();
  }

  componentDidLoad() {
    this.setCssVariables();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("isOpened")
  blockBody() {
    if (this.isOpened) {
      document.querySelector("body").style.overflow = "hidden";
    } else {
      document.querySelector("body").style.overflow = "";
    }
  }

  @Listen("resize", { target: "window" })
  onWindowResize() {
    this.checkScreenSize();
  }

  private checkScreenSize() {
    {
      /* 272 is a 48px margin + 60px contact bar width + 24px padding twice */
    }
    this.isLargeScreen = this.maximumContentWidth < window.innerWidth - 272;
  }

  private onHeaderClick() {
    this.isOpened = !this.isOpened;
  }

  private handleKeydown(event) {
    if (event.key === KeyIds.SPACE || event.key === KeyIds.ENTER) {
      this.isOpened = !this.isOpened;
    }
  }

  /**
   * Close drawer on click outside of content wrapper
   * @param event
   * @private
   */
  private handleBackgroundClick(event) {
    const clickTarget = event.target; // Here is always a hostelement, not a clicked one
    const mainElement = this.hostElement.shadowRoot?.querySelector(".eonui-bottom-drawer");

    if (clickTarget === mainElement) {
      this.isOpened = false;
      clickTarget.blur();
    }
  }

  render() {
    return (
      <Host>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
        <div
          class={pfx({
            "bottom-drawer": true,
            "bottom-drawer-open": this.isOpened
          })}
          onClick={(event) => this.handleBackgroundClick(event)}
          tabIndex={-1}
          style={{
            "--maxContentWidth": `${this.maximumContentWidth}px`
          }}
        >
          <div class={pfx("bottom-drawer-wrapper")}>
            <div class={pfx("bottom-drawer-header")}>
              <div
                class={pfx({
                  "bottom-drawer-header-wrapper": true,
                  "bottom-drawer-header-wrapper-centered": this.headlineCentered,
                  "bottom-drawer-header-wrapper-large-screen": this.isLargeScreen
                })}
                ref={(ref) => {
                  this.header = ref;
                }}
                role="button"
                tabindex="0"
                onClick={() => this.onHeaderClick()}
                onKeyDown={(e: KeyboardEvent) => this.handleKeydown(e)}
              >
                <div class={pfx("bottom-drawer-header-content")}>
                  {this.isOpened ? this.closeText : this.headerText}
                  <eon-ui-icon size="normal" name="chevron_small_up"></eon-ui-icon>
                </div>
              </div>
            </div>
            <div class={pfx("bottom-drawer-content")}>
              <slot />
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
